// As colunas do quadro do funil: uma por etapa, com os cards dela. (#880)

import { useMemo, useRef, type CSSProperties, type ReactNode } from 'react';
import type { AtendimentoDoSisac, BoardDoFunil, CardDoFunil } from '@cliqcrm/contracts';
import { dinheiro } from '@/shared/lib/formato';
import { etapaVisivel, podeTentarMover, type MovimentosEmCurso } from '../lib/mover';
import { useArrastoComGravidade } from '../model/usar-arrasto-com-gravidade';
import { Chip } from '@/shared/ui';
import { nomeDaEtapa } from '@/entities/comercial/etapa';
import { Card } from './card';

/** O que a vaga tracejada diz, pela coluna: fechar e perder dizem o que fazem. */
function textoDaVaga(resultado: 'ganho' | 'perdido' | null): string {
  if (resultado === 'ganho') return 'Solte aqui para fechar';
  if (resultado === 'perdido') return 'Solte aqui para marcar como perdida';
  return 'Solte aqui';
}

export function Colunas({
  board,
  outrosFunis,
  emCurso,
  arrastando,
  aoArrastar,
  aoMover,
  aoAbrirConversa,
  aoCriarTarefa,
  aoEditar,
  aoExcluir,
  excluidas,
  atendimentos,
  aoAbrir,
}: {
  board: BoardDoFunil;
  outrosFunis: readonly { nome: string; etapas: readonly { etapa: string; rotulo: string }[] }[];
  emCurso: MovimentosEmCurso;
  arrastando: string | null;
  aoArrastar: (cardId: string | null) => void;
  aoMover: (card: CardDoFunil, etapa: string) => void;
  aoAbrirConversa: (conversaId: string) => void;
  aoCriarTarefa: (card: CardDoFunil) => void;
  aoEditar: (card: CardDoFunil) => void;
  aoExcluir: (card: CardDoFunil) => void;
  /** Excluídas nesta tela, que somem antes de a projeção confirmar. */
  excluidas: ReadonlySet<string>;
  /** O que cada card é no SISAC (#1151), pela `referencia_externa`. */
  atendimentos: ReadonlyMap<string, AtendimentoDoSisac>;
  aoAbrir: (card: CardDoFunil) => void;
}): ReactNode {
  const colunas = useMemo(
    () => [...board.colunas].sort((a, b) => a.posicao - b.posicao),
    [board.colunas],
  );

  /** Todos os cards, para achar o arrastado sem procurar coluna por coluna. */
  const porId = useMemo(() => {
    const mapa = new Map<string, { card: CardDoFunil; aberta: boolean }>();
    for (const coluna of colunas) {
      for (const card of coluna.cards) {
        if (card.id) mapa.set(card.id, { card, aberta: coluna.aberta });
      }
    }
    return mapa;
  }, [colunas]);

  const nomesDasEtapas = colunas.map((c) => ({ etapa: c.etapa, rotulo: nomeDaEtapa(c.rotulo) }));

  /*
   * Quem acabou de entrar numa coluna vai para o TOPO dela, e fica lá nesta
   * tela: é onde o card pousou, e ele não pode pular para outro lugar quando o
   * servidor confirmar. A ordem do servidor vale de novo na próxima carga.
   */
  const entrouAgora = useRef(new Map<string, { etapa: string; em: number }>());
  /*
   * Só vale enquanto o card está de fato na coluna em que foi solto: se o
   * movimento voltou (a perda cancelada, o servidor recusou), ele perde o topo.
   */
  const quandoEntrou = (card: CardDoFunil): number => {
    const entrada = entrouAgora.current.get(card.referencia_externa);
    if (!entrada) return 0;
    // Um minuto no topo: depois, a ordem do servidor volta a valer.
    if (Date.now() - entrada.em > 60_000) {
      entrouAgora.current.delete(card.referencia_externa);
      return 0;
    }
    return entrada.etapa === etapaVisivel(card, emCurso) ? entrada.em : 0;
  };

  const arrasto = useArrastoComGravidade<CardDoFunil>({
    etapaDe: (card) => etapaVisivel(card, emCurso),
    chaveDe: (card) => card.referencia_externa,
    podeSoltar: (card, etapa) => {
      const origem = colunas.find((c) => c.etapa === etapaVisivel(card, emCurso));
      const destino = colunas.find((c) => c.etapa === etapa);
      if (!destino) return false;
      return podeTentarMover(card, { etapa: card.etapa, aberta: origem?.aberta ?? true }, destino)
        .pode;
    },
    aoSoltar: (card, etapa) => {
      entrouAgora.current.set(card.referencia_externa, { etapa, em: Date.now() });
      aoMover(card, etapa);
    },
    aoMudar: (card) => aoArrastar(card?.id ?? null),
  });

  return (
    <div className="quadro" role="list" aria-label="Etapas do funil">
      {colunas.map((coluna, indice) => {
        // Os cards que APARECEM nesta coluna, considerando o otimismo: um card
        // arrastado sai da origem e entra no destino antes da resposta.
        const cards = colunas
          .flatMap((outra) => outra.cards)
          .filter((card) => !(card.id && excluidas.has(card.id)))
          .filter((card) => etapaVisivel(card, emCurso) === coluna.etapa)
          .sort((a, b) => quandoEntrou(b) - quandoEntrou(a));
        /*
         * Total e valor da coluna vêm do servidor, e só mudam quando a projeção
         * recebe o aviso do CRM. Os excluídos nesta tela saem das contas já,
         * para o cabeçalho não contar um card que ninguém vê.
         */
        const saidas = coluna.cards.filter((card) => card.id && excluidas.has(card.id));
        const total = Math.max(0, coluna.total - saidas.length);
        const valorTotal = Math.max(
          0,
          coluna.valor_total_micros -
            saidas.reduce((soma, card) => soma + (card.valor?.quantia_micros ?? 0), 0),
        );

        const arrastado = arrastando ? porId.get(arrastando) : undefined;
        const veredito = arrastado
          ? podeTentarMover(
              arrastado.card,
              { etapa: arrastado.card.etapa, aberta: arrastado.aberta },
              coluna,
            )
          : null;

        return (
          <section
            className="coluna"
            role="listitem"
            key={coluna.etapa}
            data-etapa-alvo={coluna.etapa}
            data-alvo={
              arrasto.alvo === coluna.etapa || (!arrasto.noAr && veredito?.pode) ? 'sim' : undefined
            }
            data-proibido={veredito && !veredito.pode ? 'sim' : undefined}
            data-indice={indice}
          >
            <header className="coluna__cabecalho">
              <span className="coluna__nome">
                <span className="coluna__rotulo">{nomeDaEtapa(coluna.rotulo)}</span>
                <span
                  className="coluna__contador"
                  aria-label={`${total} ${total === 1 ? 'oportunidade' : 'oportunidades'}`}
                >
                  {total}
                </span>
                {!coluna.aberta && (
                  <Chip title="Etapa final: não se arrasta para fora dela">final</Chip>
                )}
              </span>
              {valorTotal > 0 && (
                <span className="coluna__valor">
                  {dinheiro({ quantia_micros: valorTotal, moeda: 'BRL' })}
                </span>
              )}
            </header>

            <div className="coluna__cards">
              {/*
                A vaga onde o card vai pousar: no topo, do tamanho dele, só na
                coluna sob o ponteiro.
              */}
              {arrasto.alvo === coluna.etapa && (
                <div
                  className="coluna__vaga"
                  style={{ '--altura-da-vaga': `${arrasto.altura}px` } as CSSProperties}
                  aria-hidden="true"
                >
                  {textoDaVaga(coluna.resultado)}
                </div>
              )}
              {cards.length === 0 && arrasto.alvo !== coluna.etapa ? (
                <p className="coluna__vazia">
                  Nenhuma oportunidade aqui.
                  <br />
                  Arraste um card para esta etapa.
                </p>
              ) : (
                cards.map((card) => (
                  <Card
                    key={card.referencia_externa}
                    card={card}
                    atendimento={atendimentos.get(card.referencia_externa) ?? null}
                    resultado={coluna.resultado}
                    aoSegurar={arrasto.segurar}
                    noAr={
                      (arrasto.noAr !== null &&
                        arrasto.noAr.referencia_externa === card.referencia_externa) ||
                      arrasto.pousando === card.referencia_externa
                    }
                    etapas={nomesDasEtapas}
                    outrosFunis={outrosFunis}
                    emMovimento={card.id ? emCurso.has(card.id) : false}
                    aoMover={aoMover}
                    aoAbrirConversa={aoAbrirConversa}
                    aoCriarTarefa={aoCriarTarefa}
                    aoEditar={aoEditar}
                    aoExcluir={aoExcluir}
                    aoAbrir={aoAbrir}
                  />
                ))
              )}

              {coluna.tem_mais && (
                // O board traz uma fatia por coluna. Dizer isso é melhor que a
                // contagem do cabeçalho não bater com o que se vê.
                <p className="coluna__mais">
                  Mostrando {cards.length} de {total}. Use a busca ou os filtros para encontrar o
                  resto.
                </p>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
